import { useState, CSSProperties, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  ArrowRight,
  Rss,
  MailCheck,
  Sparkles,
  Clock,
  Loader2,
} from 'lucide-react'
import { useScrollObserver } from '@/hooks/use-scroll-observer'
import { cn } from '@/lib/utils'
import { useAuth } from '@/hooks/use-auth'
import { getSubscription } from '@/services/subscription'
import { PricingSection } from '@/components/PricingSection'

const AnimatedDiv = ({
  children,
  className,
  style,
}: {
  children: React.ReactNode
  className?: string
  style?: CSSProperties
}) => {
  const [ref, isVisible] = useScrollObserver({ threshold: 0.1 })
  return (
    <div
      ref={ref}
      className={cn(
        'transition-all duration-700 ease-out',
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8',
        className,
      )}
      style={style}
    >
      {children}
    </div>
  )
}

const HeroSection = () => (
  <section className="relative py-32 md:py-48 text-center overflow-hidden">
    <div className="absolute top-0 left-1/2 -z-10 h-full w-full -translate-x-1/2 -translate-y-1/2 bg-[radial-gradient(50%_50%_at_50%_50%,hsl(var(--primary)/0.15)_0%,transparent_100%)]" />
    <div className="container mx-auto px-4 z-10">
      <AnimatedDiv>
        <h1 className="text-4xl md:text-6xl font-bold tracking-tighter mb-6 text-transparent bg-clip-text bg-gradient-to-br from-foreground to-muted-foreground">
          Sua dose diária de conteúdo, simplificada.
        </h1>
      </AnimatedDiv>
      <AnimatedDiv style={{ transitionDelay: '150ms' }}>
        <p className="max-w-2xl mx-auto text-lg md:text-xl text-muted-foreground mb-8">
          Conecte seus feeds RSS favoritos e receba um resumo inteligente direto
          no seu e-mail, todos os dias. Mantenha-se informado, sem esforço.
        </p>
      </AnimatedDiv>
      <AnimatedDiv
        className="flex flex-col sm:flex-row gap-4 justify-center"
        style={{ transitionDelay: '300ms' }}
      >
        <Button size="lg" asChild>
          <Link to="/signup">
            Comece Grátis <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
        <Button size="lg" variant="outline" asChild>
          <a href="#pricing">Ver Preços</a>
        </Button>
      </AnimatedDiv>
    </div>
  </section>
)

const FeaturesSection = () => {
  const features = [
    {
      icon: Rss,
      title: 'Feeds Centralizados',
      description:
        'Adicione até 10 fontes de conteúdo (RSS) para nunca mais perder uma atualização importante.',
    },
    {
      icon: MailCheck,
      title: 'Resumos Inteligentes',
      description:
        'Receba um único e-mail diário com os destaques de todos os seus feeds, de forma clara e concisa.',
    },
    {
      icon: Sparkles,
      title: 'Tópicos Prioritários',
      description:
        'Destaque os assuntos mais importantes para você e garanta que eles sempre apareçam no topo do seu resumo.',
    },
    {
      icon: Clock,
      title: 'Agendamento Flexível',
      description:
        'Escolha o melhor horário para receber sua newsletter e integre a leitura à sua rotina diária.',
    },
  ]
  return (
    <section className="py-20 md:py-32 bg-card/50">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Tudo que você precisa, em um só lugar.
          </h2>
          <p className="text-lg text-muted-foreground">
            Briefly foi desenhado para otimizar seu tempo e manter você sempre à
            frente.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <AnimatedDiv
              key={feature.title}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div className="text-left p-6 rounded-lg h-full bg-card/50">
                <div className="mb-4 text-primary">
                  <feature.icon className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </div>
            </AnimatedDiv>
          ))}
        </div>
      </div>
    </section>
  )
}

const HowItWorksSection = () => {
  const steps = [
    {
      step: '01',
      title: 'Cadastre seus Feeds',
      description:
        'Adicione os links dos seus blogs, sites de notícias e fontes favoritas.',
    },
    {
      step: '02',
      title: 'Configure sua Newsletter',
      description:
        'Defina os e-mails que receberão o resumo e o melhor horário para o envio.',
    },
    {
      step: '03',
      title: 'Receba e Aproveite',
      description:
        'Pronto! Seu resumo diário e personalizado chegará na sua caixa de entrada.',
    },
  ]
  return (
    <section className="py-20 md:py-32">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Comece em 3 Passos Simples
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8 relative">
          <div className="hidden md:block absolute top-1/2 left-0 w-full h-px bg-border -translate-y-1/2" />
          {steps.map((step, index) => (
            <AnimatedDiv
              key={step.title}
              style={{ transitionDelay: `${index * 150}ms` }}
              className="relative"
            >
              <Card className="text-center h-full bg-card/80 backdrop-blur-sm p-8">
                <CardHeader>
                  <div className="mx-auto text-5xl font-bold text-primary mb-4">
                    {step.step}
                  </div>
                  <CardTitle>{step.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{step.description}</p>
                </CardContent>
              </Card>
            </AnimatedDiv>
          ))}
        </div>
      </div>
    </section>
  )
}

const CTASection = () => (
  <section className="py-20 md:py-32">
    <div className="container mx-auto px-4 text-center">
      <AnimatedDiv>
        <h2 className="text-3xl md:text-4xl font-bold mb-4">
          Pronto para simplificar sua leitura?
        </h2>
        <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
          Crie sua conta gratuita e comece a receber resumos inteligentes hoje
          mesmo.
        </p>
        <Button size="lg" asChild>
          <Link to="/signup">
            Começar Agora <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </AnimatedDiv>
    </div>
  </section>
)

export default function Index() {
  const { user, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [isChecking, setIsChecking] = useState(true)

  useEffect(() => {
    if (authLoading) return

    if (user) {
      getSubscription().then((subscription) => {
        if (subscription) {
          navigate('/dashboard', { replace: true })
        } else {
          navigate('/subscribe', { replace: true })
        }
      })
    } else {
      setIsChecking(false)
    }
  }, [user, authLoading, navigate])

  if (isChecking) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <>
      <HeroSection />
      <FeaturesSection />
      <HowItWorksSection />
      <PricingSection />
      <CTASection />
    </>
  )
}
